import Layout from "@/components/Layout";

const RefundPolicy = () => {
  return (
    <Layout>
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
                Refund Policy
              </h1>
              <p className="text-muted-foreground">
                Last updated: 26 September, 2025.
              </p>
            </div>

            <div className="prose prose-lg max-w-none space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  1. General Refund Policy
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    TCLA-X is committed to participant satisfaction. Our refund
                    policy is designed to be fair while ensuring the
                    sustainability of our training programs. Refunds are
                    processed based on the timing of cancellation requests and
                    specific circumstances.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  2. Training Program Refunds
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong>Cancellation Timeline and Refund Rates:</strong>
                  </p>
                  <div className="bg-muted/30 p-6 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-semibold text-foreground">
                          30+ days before start
                        </p>
                        <p>100% refund minus 10% administrative fee</p>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">
                          15-29 days before start
                        </p>
                        <p>75% refund</p>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">
                          7-14 days before start
                        </p>
                        <p>50% refund</p>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">
                          Less than 7 days
                        </p>
                        <p>No monetary refund*</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm">
                    *Participants canceling within 7 days may transfer to a
                    future program at no additional cost.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  3. Exception Circumstances
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>Full refunds may be provided regardless of timing for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Medical emergencies (with valid documentation)</li>
                    <li>Bereavement in immediate family</li>
                    <li>Natural disasters or government travel restrictions</li>
                    <li>Program cancellation by TCLA-X</li>
                    <li>
                      Significant changes to program content or schedule by
                      TCLA-X
                    </li>
                  </ul>
                  <p>
                    Supporting documentation may be required for exception
                    circumstances.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  4. Corporate Training Refunds
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Corporate training programs have customized refund terms
                    based on:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Program scope and customization level</li>
                    <li>Resources committed by TCLA-X</li>
                    <li>Advance notice provided</li>
                    <li>Reason for cancellation</li>
                  </ul>
                  <p>
                    Corporate refund terms are outlined in individual training
                    agreements.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  5. Online Training Refunds
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>For online and digital training programs:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      Refund requests must be made within 48 hours of program
                      access
                    </li>
                    <li>
                      No refund if more than 25% of content has been accessed
                    </li>
                    <li>
                      Technical issues will be resolved rather than refunded
                      where possible
                    </li>
                    <li>
                      Full refund for programs that cannot be delivered due to
                      technical failures
                    </li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  6. Certification Program Refunds
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    Certification programs have specific refund considerations:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      Materials and assessment fees are non-refundable once
                      provided
                    </li>
                    <li>Partial completion may qualify for partial refunds</li>
                    <li>
                      Failed certification attempts do not qualify for refunds
                    </li>
                    <li>
                      Transferred certifications may incur additional
                      administrative fees
                    </li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  7. Refund Process
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>To request a refund:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Submit written request to info@tcla-x.com</li>
                    <li>
                      Include participant details and reason for cancellation
                    </li>
                    <li>Provide supporting documentation if applicable</li>
                    <li>Allow 5-10 business days for review and processing</li>
                    <li>
                      Approved refunds are processed within 14 business days
                    </li>
                  </ol>
                  <p>
                    Refunds are issued to the original payment method unless
                    otherwise specified.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  8. Transfer Options
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>As an alternative to refunds, participants may:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Transfer to another program within 12 months</li>
                    <li>Substitute another participant (corporate bookings)</li>
                    <li>Convert to online format where available</li>
                    <li>Receive program credit for future use</li>
                  </ul>
                  <p>Transfer options may incur minimal administrative fees.</p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  9. Non-Refundable Items
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    The following are non-refundable under all circumstances:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Administrative and processing fees</li>
                    <li>Third-party certification exam fees</li>
                    <li>Printed materials and resources once shipped</li>
                    <li>Travel and accommodation arranged independently</li>
                    <li>Completed coaching sessions</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  10. Contact Information
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>For refund requests or questions about this policy:</p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p>
                      <strong>Refunds Department</strong>
                    </p>
                    <p>Email: info@tcla-x.com</p>
                    <p>Phone: +254722 319 499</p>
                    <p>Address: TCLA-X Limited, Nairobi, Kenya</p>
                    <p>
                      Business Hours: Monday - Friday, 8:00 AM - 5:00 PM EAT
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RefundPolicy;
